import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Sheet,
  Stack,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/joy/styles";

import { fetchAdminEvents, AdminEvent } from "../../api";

const DATE_FORMAT = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function formatDate(date?: string | null) {
  if (!date) {
    return "—";
  }
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return DATE_FORMAT.format(parsed);
}

function getStatusColor(status?: string | null) {
  if (!status) {
    return "neutral";
  }
  const normalized = status.toLowerCase();
  if (normalized.includes("live") || normalized.includes("active")) {
    return "success";
  }
  if (normalized.includes("planning") || normalized.includes("draft")) {
    return "primary";
  }
  if (normalized.includes("complete") || normalized.includes("past")) {
    return "neutral";
  }
  return "warning";
}

export default function AdminEvents() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [events, setEvents] = React.useState<AdminEvent[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const eventSummary = React.useMemo(() => {
    const total = events.length;
    const visible = events.filter((event) => !event.hidden).length;
    const hidden = Math.max(total - visible, 0);
    const active = events.filter((event) => getStatusColor(event.status) === "success").length;
    return { total, visible, hidden, active };
  }, [events]);

  const loadEvents = React.useCallback(async (isRefresh = false) => {
    setError(null);
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const data = await fetchAdminEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load events", err);
      setError("We were unable to fetch the events list. Please try again.");
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const hasEvents = events.length > 0;
  const upcomingCount = events.filter((event) => !event.hidden).length;
  const statBackground = (tone: string) => {
    if (isDark) {
      if (tone === "primary") {
        return "linear-gradient(135deg, rgba(30, 64, 175, 0.25), rgba(15, 23, 42, 0.95))";
      }
      if (tone === "success") {
        return "linear-gradient(135deg, rgba(22, 163, 74, 0.22), rgba(15, 23, 42, 0.95))";
      }
      if (tone === "warning") {
        return "linear-gradient(135deg, rgba(234, 179, 8, 0.22), rgba(15, 23, 42, 0.95))";
      }
      return "linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))";
    }
    if (tone === "primary") {
      return "linear-gradient(135deg, rgba(217, 236, 255, 0.9), rgba(241, 248, 255, 0.95))";
    }
    if (tone === "success") {
      return "linear-gradient(135deg, rgba(216, 247, 231, 0.9), rgba(240, 252, 246, 0.95))";
    }
    if (tone === "warning") {
      return "linear-gradient(135deg, rgba(255, 239, 213, 0.9), rgba(255, 248, 234, 0.95))";
    }
    return "linear-gradient(135deg, rgba(238, 241, 250, 0.9), rgba(246, 248, 252, 0.95))";
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        boxShadow: "sm",
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EventAvailableRoundedIcon fontSize="small" color="primary" />
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Seva calendar overview
            </Typography>
          </Stack>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 480 }}>
            Track temple programs, yatra logistics, and outreach festivals so devotees can serve with confidence.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            variant="soft"
            color={upcomingCount > 0 ? "success" : "neutral"}
            sx={{ fontWeight: 600 }}
          >
            {upcomingCount} visible {upcomingCount === 1 ? "event" : "events"}
          </Chip>
          <Tooltip title="Refresh events">
            <IconButton
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => void loadEvents(true)}
              loading={refreshing}
              sx={{ borderRadius: "lg" }}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error ? (
        <Alert color="danger" variant="soft">
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(4, minmax(0, 1fr))" },
          gap: 1.5,
        }}
      >
        {[
          { label: "Total events", value: eventSummary.total, tone: "primary" },
          { label: "Visible", value: eventSummary.visible, tone: "success" },
          { label: "Hidden", value: eventSummary.hidden, tone: "neutral" },
          { label: "Active", value: eventSummary.active, tone: "warning" },
        ].map((item) => (
          <Card
            key={item.label}
            variant="soft"
            sx={{
              borderRadius: "lg",
              p: 1.5,
              background: statBackground(item.tone),
            }}
          >
            <Typography level="body-xs" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700 }}>
              {item.label}
            </Typography>
            <Typography level="title-lg" sx={{ mt: 0.5 }}>
              {item.value}
            </Typography>
          </Card>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size="md" aria-label="Loading events" />
        </Box>
      ) : hasEvents ? (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5, mt: 1 }}
          >
            <Sheet
              variant="soft"
              color="primary"
              sx={{
                borderRadius: "lg",
                px: 1.25,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                boxShadow: "var(--joy-shadow-xs)",
              }}
            >
              <EventAvailableRoundedIcon fontSize="small" />
              <Typography level="title-sm">Events list</Typography>
            </Sheet>
            <Tooltip title="Refresh events">
              <span>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="neutral"
                  disabled={loading || refreshing}
                  onClick={() => void loadEvents(true)}
                >
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Sheet
            variant="outlined"
            sx={{
              borderRadius: "lg",
              borderColor: "divider",
              backgroundColor: "background.surface",
              maxWidth: "960px",
              overflowX: "auto",
              position: "relative",
            }}
          >
            {refreshing ? <LinearProgress /> : null}

            <Table
              stickyHeader
              hoverRow
              sx={{
                width: "100%",
                "--TableCell-paddingY": "12px",
                "--TableCell-paddingX": "16px",
                "--Table-headerUnderlineThickness": "1px",
                "& thead th": {
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                  letterSpacing: 0.6,
                  color: "text.secondary",
                  backgroundColor: "var(--joy-palette-background-level1)",
                },
                "& tbody tr": {
                  "& td": {
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }
                },
                "& tbody tr:nth-of-type(even)": {
                  backgroundColor: "var(--joy-palette-background-level1)",
                },
                "& tbody tr:hover": {
                  backgroundColor: "var(--joy-palette-background-level2)",
                },
              }}
            >
                <thead>
                  <tr>
                    <th style={{ width: 180 }}>Event</th>
                    <th style={{ width: 160 }}>Purpose</th>
                    <th style={{ width: 140 }}>Location</th>
                    <th style={{ width: 120 }}>Start date</th>
                    <th style={{ width: 120 }}>End date</th>
                    <th style={{ width: 120 }}>Status</th>
                    <th style={{ width: 120 }}>Lead</th>
                    <th style={{ width: 110 }}>Visibility</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const statusColor = getStatusColor(event.status);
                    return (
                      <tr key={event.id}>
                        <td>
                          <Stack spacing={0.5}>
                            <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                              {event.title}
                            </Typography>
                            {event.description ? (
                              <Typography level="body-xs" sx={{ color: "text.tertiary" }} noWrap>
                                {event.description}
                              </Typography>
                            ) : null}
                          </Stack>
                        </td>
                        <td>
                          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                            {event.purpose || "—"}
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-sm">{event.location || "—"}</Typography>
                        </td>
                        <td>
                          <Typography level="body-sm">{formatDate(event.startDate)}</Typography>
                        </td>
                        <td>
                          <Typography level="body-sm">{formatDate(event.endDate)}</Typography>
                        </td>
                        <td>
                          <Chip size="sm" variant="soft" color={statusColor} sx={{ textTransform: "capitalize" }}>
                            {event.status || "Draft"}
                          </Chip>
                        </td>
                        <td>
                          <Typography level="body-sm">{event.head || "—"}</Typography>
                        </td>
                        <td>
                          <Chip
                            size="sm"
                            variant={event.hidden ? "soft" : "outlined"}
                            color={event.hidden ? "neutral" : "success"}
                            sx={{ textTransform: "capitalize" }}
                          >
                            {event.hidden ? "Hidden" : "Visible"}
                          </Chip>
                        </td>
                        <td>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Link
                              level="body-xs"
                              component={RouterLink}
                              to={`/events/${event.id}`}
                            >
                              View
                            </Link>
                            <Tooltip title="Edit event">
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="primary"
                                component={RouterLink}
                                to={`/events/${event.id}/edit`}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
          </Sheet>
        </>
      ) : (
        <Sheet
          variant="soft"
          color="neutral"
          sx={{
            borderRadius: "xl",
            p: { xs: 3, md: 4 },
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
            background: isDark
              ? "linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.96))"
              : "linear-gradient(135deg, rgba(238, 241, 250, 0.9), rgba(246, 248, 252, 0.95))",
          }}
        >
          <Typography level="title-md" sx={{ fontWeight: 700 }}>
            No events scheduled yet
          </Typography>
          <Typography level="body-sm" sx={{ maxWidth: 420, color: "text.secondary" }}>
            Plan the next festival or seva drive to inspire the community. Every gathering kindles new devotion.
          </Typography>
          <Button
            component={RouterLink}
            to="/events/new"
            size="md"
            variant="soft"
            color="primary"
            sx={{ borderRadius: "lg", px: 2.5 }}
            startDecorator={<AddRoundedIcon />}
          >
            Create your first event
          </Button>
        </Sheet>
      )}
    </Card>
  );
}
